import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { CardNB, ButtonNB, BadgeNB, IconNB, SelectNB } from '../../../components/ui-nb';
import DatePickerNB from '../../../components/ui-nb/DatePickerNB';
import { useTranslation } from '../../../i18n';
import { useAdminOrdersList } from '../../../hooks/useAdminOrdersList';
import type { Tables } from '@tellowup/db';

// Intersection type for orders with branch name from the joined query
type OrderWithBranch = Tables<'orders'> & {
  branches?: { name: string } | null;
  profiles?: { id: string; full_name: string } | null;
  status_config?: { name: string } | null;
};

interface Branch {
  id: string;
  name: string;
}

type SortColumn = 'created_at' | 'customer_name' | 'device' | 'status';
type SortDirection = 'asc' | 'desc';

const STATUS_OPTIONS = [
  'Received',
  'Diagnosing',
  'Waiting for part',
  'In repair',
  'In testing',
  'Ready for delivery',
  'Delivered',
];

export default function OrdersList() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const organizationId = useAuthStore(s => s.user?.organizationId) ?? null;
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Sort state
  const [sortCol, setSortCol] = useState<SortColumn>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 20;
  
  // Branches for dropdown
  const [branches, setBranches] = useState<Branch[]>([]);
  
  const { orders, totalCount, isLoading, error, refetch } = useAdminOrdersList({
    organizationId,
    status: statusFilter,
    branchId: branchFilter,
    dateFrom,
    dateTo,
    search: searchTerm ?? null,
    sortCol,
    sortDir,
    page: currentPage,
  });

  // Fetch branches for dropdown
  useEffect(() => {
    if (!organizationId) return;

    async function fetchBranches() {
      const { supabase } = await import('../../../lib/supabase');
      const { data: branchData } = await supabase
        .from('branches')
        .select('id, name')
        .eq('organization_id', organizationId);

      if (branchData) {
        setBranches(branchData);
      }
    }

    fetchBranches();
  }, [organizationId]);

  const handleSort = (column: SortColumn) => {
    if (sortCol === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(column);
      setSortDir('desc');
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const renderSortIcon = (column: SortColumn) => {
    if (sortCol !== column) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-nb-black">Orders</h1>
        <ButtonNB variant="primary" size="sm" onClick={() => navigate(`/${lang}/technician/receive`)}>
          <IconNB name="plus" size={16} />
          Receive Equipment
        </ButtonNB>
      </div>

      {/* Filter Bar */}
      <CardNB className="p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
          {/* Status Filter */}
          <SelectNB
            label="Status"
            value={statusFilter || ''}
            onChange={(e) => {
              setStatusFilter(e.target.value || null);
              setCurrentPage(0);
            }}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </SelectNB>

          {/* Branch Filter */}
          <SelectNB
            label="Branch"
            value={branchFilter || ''}
            onChange={(e) => {
              setBranchFilter(e.target.value || null);
              setCurrentPage(0);
            }}
          >
            <option value="">All Branches</option>
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </SelectNB>

          {/* Date From */}
          <DatePickerNB
            label="From"
            value={dateFrom || ''}
            onChange={(val) => { setDateFrom(val || null); setCurrentPage(0); }}
          />

          {/* Date To */}
          <DatePickerNB
            label="To"
            value={dateTo || ''}
            onChange={(val) => { setDateTo(val || null); setCurrentPage(0); }}
          />

          {/* Search */}
          <div className="col-span-2 md:col-span-2 lg:col-span-1">
            <label className="font-nb font-medium text-sm text-nb-text-secondary block mb-1">Search</label>
            <input
              type="text"
              placeholder="Customer, device, folio..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
              className="bg-nb-white border-3 border-nb-black px-4 py-3 text-sm font-nb rounded-[2px] focus:border-nb-yellow focus:outline-none w-full"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <ButtonNB 
              variant="secondary" 
              size="sm"
              disabled={!statusFilter && !branchFilter && !dateFrom && !dateTo && !searchTerm}
              onClick={() => {
                setStatusFilter(null);
                setBranchFilter(null);
                setDateFrom(null);
                setDateTo(null);
                setSearchTerm('');
                setCurrentPage(0);
                setSortCol('created_at');
                setSortDir('desc');
              }}
            >
              Clear
            </ButtonNB>
          </div>
        </div>
      </CardNB>

      {/* Error State */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded bg-nb-red/10 text-nb-red text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <p className="text-nb-text-muted">Loading...</p>
      ) : orders.length === 0 ? (
        <CardNB className="text-center p-8">
          <p className="font-nb font-bold text-nb-text-muted mb-4">No orders found</p>
          <ButtonNB variant="primary" size="sm" onClick={() => navigate(`/${lang}/technician/receive`)}>
            <IconNB name="plus" size={16} />
            Receive Equipment
          </ButtonNB>
        </CardNB>
      ) : (
        <>
          {/* Orders Table */}
          <CardNB className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-3 border-nb-black bg-nb-cream">
                    <th 
                      className="text-left p-4 font-nb font-bold text-nb-text-muted cursor-pointer hover:bg-nb-yellow/50"
                      onClick={() => handleSort('customer_name')}
                    >
                      Customer{renderSortIcon('customer_name')}
                    </th>
                    <th 
                      className="text-left p-4 font-nb font-bold text-nb-text-muted cursor-pointer hover:bg-nb-yellow/50"
                      onClick={() => handleSort('device')}
                    >
                      Device{renderSortIcon('device')}
                    </th>
                    <th 
                      className="text-left p-4 font-nb font-bold text-nb-text-muted cursor-pointer hover:bg-nb-yellow/50"
                      onClick={() => handleSort('status')}
                    >
                      Status{renderSortIcon('status')}
                    </th>
                    <th 
                      className="text-left p-4 font-nb font-bold text-nb-text-muted cursor-pointer hover:bg-nb-yellow/50"
                      onClick={() => handleSort('created_at')}
                    >
                      Date{renderSortIcon('created_at')}
                    </th>
                    <th className="text-left p-4 font-nb font-bold text-nb-text-muted">
                      Branch
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr 
                      key={order.id}
                      className={`border-b border-nb-black/10 cursor-pointer hover:bg-nb-yellow/30 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-nb-cream/50'
                      }`}
                      onClick={() => navigate(`/${lang}/admin/orders/${order.id}`)}
                    >
                      <td className="p-4 font-nb font-medium text-nb-black">
                        {order.customer_name}
                      </td>
                      <td className="p-4 text-nb-black">
                        {order.device}
                      </td>
                      <td className="p-4">
                        <BadgeNB variant={order.status === 'COMPLETED' ? 'success' : 'warning'}>
                          {order.status_config?.name || order.status}
                        </BadgeNB>
                      </td>
                      <td className="p-4 text-nb-text-muted">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-nb-black">
                        {(order as OrderWithBranch).branches?.name || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardNB>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-nb-text-muted">
              Page {currentPage + 1} of {totalPages || 1} ({totalCount} orders)
            </p>
            <div className="flex gap-2">
              <ButtonNB 
                variant="secondary" 
                size="sm" 
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              >
                <IconNB name="chevronLeft" size={16} />
                Previous
              </ButtonNB>
              
              {/* Page Numbers */}
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage < 3) {
                    pageNum = i;
                  } else if (currentPage > totalPages - 3) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm font-nb border-2 border-nb-black rounded-[2px] ${
                        currentPage === pageNum 
                          ? 'bg-nb-black text-white' 
                          : 'bg-white text-nb-black hover:bg-nb-yellow'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>
              
              <ButtonNB 
                variant="secondary" 
                size="sm" 
                disabled={currentPage >= totalPages - 1}
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              >
                Next
                <IconNB name="chevronRight" size={16} />
              </ButtonNB>
            </div>
          </div>
        </>
      )}
    </>
  );
}
